"use client";

import { useId, useState } from "react";
import type { CFaqContent, CFaqItem } from "@/lib/api/cms-catalog";
import { Input } from "@/components/ui/Form";
import { Icon } from "@/lib/icons/Icon";
import { useT } from "@/lib/i18n/context";
import { cFaqTranslations, type CFaqTranslations } from "./CFaq.i18n";
import styles from "./CFaq.module.css";

/**
 * CFaq - visitor view komponentu `c_faq` (Najczęściej zadawane pytania).
 *
 * Migracja z legacy Smarty templatu `c_faq_default_modern.imp` + jQuery
 * `slideToggle` na czystego Reacta - pojedynczy panel sterowany `useState`
 * (multi-open: każde pytanie ma niezależny stan rozwinięcia).
 *
 * **Data source:** `C_FAQ_CONTROLLER::getApiContent()` zwraca:
 *   ```
 *   { id, symbol, subtitle, items: [{ id, question, answer }, ...] }
 *   ```
 *
 * **Akordeon:** kliknięcie pytania toggle'uje rozwinięcie odpowiedzi.
 * `aria-expanded` + `aria-controls` na buttonie pytania + `role="region"`
 * na panelu odpowiedzi - SR ogłasza zmianę stanu. Klawiatura: button
 * obsługuje Enter/Spację natywnie.
 *
 * **Security:** `item.answer` to HTML z admin panel (IHTMLEditor w
 * `c_faq_admin.php`) - trusted source, OK dla `dangerouslySetInnerHTML`.
 */
export function CFaq({ content }: CFaqProps) {
  const t = useT(cFaqTranslations);
  const [query, setQuery] = useState("");
  const searchId = useId();

  if (!content || typeof content !== "object") {
    return <div className={styles.empty}>{t.emptyData}</div>;
  }

  const items: CFaqItem[] = Array.isArray(content.items) ? content.items : [];
  const subtitle = content.subtitle?.trim() ?? "";

  const normalizedQuery = query.trim().toLowerCase();
  const filtered = normalizedQuery
    ? items.filter((item) => buildHaystack(item).includes(normalizedQuery))
    : items;

  return (
    <div className={styles.root}>
      {subtitle && (
        <div className={styles.subtitle}>
          <span>{subtitle}</span>
        </div>
      )}

      {items.length > 0 && (
        <div role="search">
          <Input
            id={searchId}
            type="search"
            aria-label={t.searchLabel}
            placeholder={t.searchPlaceholder}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoComplete="off"
            spellCheck={false}
            prefix={<Icon name="magnifying-glass" />}
          />
        </div>
      )}

      {items.length === 0 ? (
        <div className={styles.empty}>{t.emptyList}</div>
      ) : filtered.length === 0 ? (
        <div className={styles.empty} role="status" aria-live="polite">
          {t.formatEmptyResults(query.trim())}
        </div>
      ) : (
        <ul className={styles.list}>
          {filtered.map((item, index) => (
            <FaqRow key={item.id} item={item} index={index + 1} t={t} />
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Frontend haystack do wyszukiwarki: pytanie + tekst odpowiedzi z odartymi
 * tagami HTML, wszystko lowercase. `item.answer` pochodzi z IHTMLEditor
 * (trusted admin), więc prosty regex do stripowania wystarczy - użytkownik
 * i tak wpisuje plain text, nie znaczniki.
 */
function buildHaystack(item: CFaqItem): string {
  const answerText = item.answer.replace(/<[^>]*>/g, " ");
  return `${item.question} ${answerText}`.toLowerCase();
}

export type CFaqProps = {
  content: CFaqContent;
};

function FaqRow({
  item,
  index,
  t,
}: {
  item: CFaqItem;
  index: number;
  t: CFaqTranslations;
}) {
  const [open, setOpen] = useState(false);
  const panelId = useId();

  return (
    <li className={`${styles.row} ${open ? styles.rowActive : ""}`}>
      <button
        type="button"
        className={styles.question}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((v) => !v)}
      >
        <span className={styles.questionLabel}>
          {t.formatQuestion(index, item.question)}
        </span>
        <span className={styles.chevron} aria-hidden="true">
          <ChevronDown />
        </span>
      </button>
      <div
        id={panelId}
        role="region"
        hidden={!open}
        className={styles.answer}
        dangerouslySetInnerHTML={{ __html: item.answer }}
      />
    </li>
  );
}

function ChevronDown() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="15"
      height="15"
      fill="currentColor"
      focusable="false"
    >
      <path d="M12 16.5 3.75 8.25l1.5-1.5L12 13.5l6.75-6.75 1.5 1.5z" />
    </svg>
  );
}
